import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Crosshair, MapPin, X } from 'lucide-react';
import { useDialogA11y } from '../lib/useDialogA11y';
import { sanitizeAndCompressPhoto } from '../lib/exif-sanitizer';
import { selectableDates, submitRaccoonReport, type RaccoonReportType } from '../lib/raccoon';

export interface PickedPoint {
  lat: number;
  lng: number;
  source: 'gps' | 'map';
  accuracy?: number;
}

interface Props {
  isOpen: boolean;
  point: PickedPoint | null;
  onPickOnMap: () => void;
  onPointChange: (point: PickedPoint | null) => void;
  onClose: () => void;
  onSaved: (weekLabel: string) => void;
}

function weekOf(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `Uge ${week}, ${t.getUTCFullYear()}`;
}

const dateLabel = (iso: string, index: number) =>
  index === 0
    ? 'I dag'
    : index === 1
      ? 'I går'
      : new Date(`${iso}T12:00:00`).toLocaleDateString('da-DK', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });

export default function RaccoonReportModal({
  isOpen,
  point,
  onPickOnMap,
  onPointChange,
  onClose,
  onSaved,
}: Props) {
  const dates = selectableDates();
  const [type, setType] = useState<RaccoonReportType | null>(null);
  const [date, setDate] = useState(dates[0]);
  const [photo, setPhoto] = useState<File | null>(null);
  const [locating, setLocating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { ref: dialogRef } = useDialogA11y({ open: isOpen, onClose });

  if (!isOpen) return null;

  const useGps = () => {
    if (!navigator.geolocation) {
      setError('Din enhed kan ikke give din position. Vælg i stedet på kortet.');
      return;
    }
    setError('');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onPointChange({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          source: 'gps',
          accuracy: Math.round(pos.coords.accuracy),
        });
      },
      () => {
        setLocating(false);
        setError('Positionen kunne ikke findes. Giv adgang til lokation, eller vælg på kortet.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const submit = async () => {
    if (!point || !type || busy) return;
    setBusy(true);
    setError('');
    try {
      const clean = photo ? await sanitizeAndCompressPhoto(photo) : null;
      await submitRaccoonReport({
        type,
        lat: point.lat,
        lng: point.lng,
        observedOn: date,
        photo: clean,
      });
      setType(null);
      setPhoto(null);
      setDate(dates[0]);
      onSaved(weekOf(date));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Indberetningen kunne ikke gemmes.');
    } finally {
      setBusy(false);
    }
  };

  // Dialogen lægges direkte i body: sidens indholdscontainer har en transform, som ellers gør
  // position: fixed relativ til containeren (og dermed skjult bag top- og bundmenu).
  return createPortal(
    <div
      ref={dialogRef}
      className="rk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rk-title"
    >
      <div className="rk-sheet">
        <header className="rk-sheet-head">
          <h2 id="rk-title">Indberet mårhund</h2>
          <button type="button" className="rk-icon-btn" onClick={onClose} aria-label="Luk">
            <X size={20} />
          </button>
        </header>

        <section className="rk-step">
          <h3>1. Hvor?</h3>
          <div className="rk-row">
            <button type="button" className="rk-btn" onClick={useGps} disabled={locating}>
              <Crosshair size={18} /> {locating ? 'Finder position…' : 'Brug min position'}
            </button>
            <button type="button" className="rk-btn" onClick={onPickOnMap}>
              <MapPin size={18} /> Vælg på kortet
            </button>
          </div>
          {point && (
            <p className="rk-ok">
              Position valgt
              {point.source === 'gps' && point.accuracy ? ` (±${point.accuracy} m)` : ''}. Kun et
              område på 2 × 2 km bliver vist for andre.
            </p>
          )}
        </section>

        <section className="rk-step">
          <h3>2. Hvad?</h3>
          <div className="rk-row">
            <button
              type="button"
              className={`rk-btn rk-choice ${type === 'observed' ? 'is-on' : ''}`}
              aria-pressed={type === 'observed'}
              onClick={() => setType('observed')}
            >
              Observeret
            </button>
            <button
              type="button"
              className={`rk-btn rk-choice ${type === 'harvested' ? 'is-on' : ''}`}
              aria-pressed={type === 'harvested'}
              onClick={() => setType('harvested')}
            >
              Nedlagt
            </button>
          </div>
        </section>

        <section className="rk-step">
          <h3>3. Hvornår?</h3>
          <select
            className="rk-select"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Dato"
          >
            {dates.map((d, i) => (
              <option key={d} value={d}>
                {dateLabel(d, i)}
              </option>
            ))}
          </select>
        </section>

        <section className="rk-step">
          <h3>4. Foto (valgfrit)</h3>
          <label className="rk-btn rk-file">
            <Camera size={18} /> {photo ? photo.name : 'Tilføj foto'}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
          </label>
          <p className="rk-note">
            Fotoet renses for position og kameradata, før det uploades. Det er kun synligt for dig
            og administratorer, og det regnes ikke som verificeret dokumentation.
          </p>
        </section>

        {error && (
          <p role="alert" className="rk-error">
            {error}
          </p>
        )}
        <button
          type="button"
          className="rk-btn rk-primary"
          disabled={!point || !type || busy}
          onClick={() => void submit()}
        >
          {busy ? 'Gemmer…' : 'Gem indberetning'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
